/**
 * Single source of truth for UI languages: codes, display names, RTL set,
 * and lazy locale-bundle loaders. Enumeration comes from the filesystem so
 * the picker can never drift from what actually ships.
 */

const localeModules = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/hireport-*.json',
);

/** Stale Norwegian file — Bokmål is covered by `nb`. */
const EXCLUDED = new Set(['no']);

const PREFIX = './locales/hireport-';
const SUFFIX = '.json';

export const LANGUAGE_CODES = Object.keys(localeModules)
  .map((path) => path.slice(PREFIX.length, -SUFFIX.length))
  .filter((code) => !EXCLUDED.has(code))
  .sort();

const RTL_LANGUAGES = new Set(['ar', 'he']);

export const isRtlLanguage = (code: string): boolean => RTL_LANGUAGES.has(code);

/**
 * English fallback names for Intl.DisplayNames (older engines / SSR).
 * Prefer endonyms from Intl when available.
 */
const ENGLISH_LANGUAGE_NAMES: Record<string, string> = {
  af: 'Afrikaans',
  ar: 'Arabic',
  bg: 'Bulgarian',
  cs: 'Czech',
  da: 'Danish',
  de: 'German',
  el: 'Greek',
  en: 'English',
  es: 'Spanish',
  et: 'Estonian',
  fi: 'Finnish',
  fr: 'French',
  he: 'Hebrew',
  hr: 'Croatian',
  hu: 'Hungarian',
  id: 'Indonesian',
  it: 'Italian',
  ja: 'Japanese',
  ka: 'Georgian',
  ko: 'Korean',
  lt: 'Lithuanian',
  lv: 'Latvian',
  nb: 'Norwegian Bokmål',
  nl: 'Dutch',
  pl: 'Polish',
  pt: 'Portuguese',
  ro: 'Romanian',
  ru: 'Russian',
  sk: 'Slovak',
  sl: 'Slovenian',
  sr: 'Serbian',
  sv: 'Swedish',
  th: 'Thai',
  tr: 'Turkish',
  uk: 'Ukrainian',
  vi: 'Vietnamese',
  zh: 'Chinese',
};

let displayNames: Intl.DisplayNames | null = null;

try {
  displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
} catch {
  displayNames = null;
}

/** Capitalize the first grapheme with the language's own casing rules. */
function capitalizeDisplayName(name: string, locale: string): string {
  const chars = Array.from(name);
  if (chars.length === 0) {
    return name;
  }
  chars[0] = chars[0].toLocaleUpperCase(locale);
  return chars.join('');
}

/** Native/endonym display name when Intl is available; else English fallback. */
export function getLanguageDisplayName(code: string): string {
  if (displayNames) {
    try {
      const endonym = new Intl.DisplayNames([code], { type: 'language' }).of(
        code,
      );
      if (endonym) {
        return capitalizeDisplayName(endonym, code);
      }
    } catch {
      // fall through
    }
    const english = displayNames.of(code);
    if (english) {
      return capitalizeDisplayName(english, 'en');
    }
  }
  return ENGLISH_LANGUAGE_NAMES[code] ?? code.toUpperCase();
}

export async function loadLocaleBundle(
  code: string,
): Promise<Record<string, unknown>> {
  const path = `${PREFIX}${code}${SUFFIX}`;
  const loader = localeModules[path];
  if (!loader) {
    throw new Error(`Unknown locale bundle: ${code}`);
  }
  const mod = await loader();
  return mod.default;
}
